/**
 * Shared date-display parsing/formatting for the reports module.
 *
 * Handles the value shapes seen across reports API responses: unix seconds/ms sent
 * as a numeric string (e.g. payment-log `created_at`), bare "YYYY-MM-DD" dates, and
 * "YYYY-MM-DD HH:mm:ss" / ISO datetime strings.
 */

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Parses a report date value into a `Date`, or `null` if it can't be parsed.
 * Bare "YYYY-MM-DD" values are anchored to local noon so they don't shift a day
 * in negative-UTC-offset timezones (the value has no time component to preserve).
 */
export function parseReportDate(value: unknown): Date | null {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return null

  let d: Date
  if (/^\d{10,13}$/.test(trimmed)) {
    const n = Number(trimmed)
    d = trimmed.length === 10 ? new Date(n * 1000) : new Date(n)
  } else {
    const dateOnly = trimmed.match(/^(\d{4}-\d{2}-\d{2})$/)
    d = dateOnly
      ? new Date(`${dateOnly[1]}T12:00:00`)
      : new Date(trimmed.replace(' ', 'T'))
  }
  return Number.isNaN(d.getTime()) ? null : d
}

/** MM/DD/YYYY — falls back to the raw value (or `fallback` when empty) if unparseable. */
export function formatReportDate(value: unknown, fallback = '—'): string {
  if (value == null || value === '') return fallback
  const d = parseReportDate(value)
  if (!d) return String(value)
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`
}
